import React, { useState } from "react";
import { Box, Button, Stack, TextField, Typography, useTheme } from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntl } from "react-intl";
import AxionControls from "../../../components/controls/AxionControls";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../cmmsRoles";
import { useLocalStorage } from "../../../hooks/useLocalStorage";
import { resetRecurringSchedule } from "../../../fetchers/cmmsRecurringSchedule";
import { pushErrorMessage, pushNotification } from "../../../utils/notifications";
import cmmsMessages from "../cmmsMessages";
import { formatCmmsError } from "./CmmsStates";
import { buildScheduleResetWarning, isResettableSchedule } from "../scheduleReset";
import { getAssetHierarchyPalette, getOutlinedButtonSx } from "../assetHierarchyStyles";

// Reset is not undo. The log entry it writes is the only record that the consumption before it
// ever existed, so the gesture is confirmed rather than immediate — and the confirmation names
// the figure, which is what makes it a decision instead of a dare.
const NOTE_MAX_LENGTH = 2000;

/*
 * Reset one schedule's counter: the gate, the confirmation, the note and the mutation.
 *
 * Mounted twice — from the schedules page's reset drawer, and from the CMMS tool register's
 * service drawer, where a technician actually notices a tool at 98 %. One component rather than
 * two call sites of the same three hooks: the two would drift, and the half that drifts is
 * whichever one nobody opened this week.
 *
 * `reading` is the normalized shape from scheduleReset.js, and may be null. That is not an error
 * state — it means the reading has not arrived or the schedule is watched by nothing — and the
 * confirmation then omits the figure rather than inventing a zero.
 *
 * `lastResetAt` is optional and comes from the caller's own log, where it has one. It is never
 * derived from IntervalStartingDate; see the note in buildScheduleResetWarning.
 *
 * There is no onSuccess callback, deliberately. Everything a reset moved is invalidated below by
 * prefix, and both mount points read through those keys — so a caller that also refetched would
 * be firing a second request for a list react-query is already refreshing, and the two mount
 * points would have two different ideas of what "done" means.
 */
export default function ScheduleResetButton({
  schedule,
  reading,
  lastResetAt,
  size = "small",
  fullWidth = false,
}) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const queryClient = useQueryClient();
  const [companyKey] = useLocalStorage("companyKey", 1);

  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");

  const mutation = useMutation({
    mutationFn: () => resetRecurringSchedule(companyKey, schedule.id, notes.trim()),
  });

  /*
   * Everything the reset moved, by prefix.
   *
   * The last two are what keep the two mount points honest about each other: a reset made on the
   * schedules page has to leave the tool register showing the new bar, and a reset made in the
   * tool drawer has to leave the schedule's log carrying the new entry. A tool-lane reset writes
   * a ToolServiceLog, so cmmsToolServiceHistory is not an extra — it is where that entry lands.
   */
  const invalidate = () => {
    ["cmmsScheduleResetLog", "cmmsScheduleConsumption", "cmmsToolServiceStatuses",
      "cmmsToolServiceHistory", "cmmsRecurringSchedules"].forEach((key) => {
      queryClient.invalidateQueries({ queryKey: [key] });
    });
  };

  const close = () => {
    setOpen(false);
    // Cleared on the way out, not on the way in: a note typed and then abandoned must not turn up
    // pre-filled over the next schedule's confirmation.
    setNotes("");
  };

  const handleConfirm = async () => {
    try {
      await mutation.mutateAsync();
      close();
      pushNotification(intl.formatMessage(cmmsMessages.resetToastDone));
      invalidate();
    } catch (error) {
      pushErrorMessage(formatCmmsError(error, intl.formatMessage(cmmsMessages.resetFailed)));
    }
  };

  // A calendar schedule has no counter, and the server answers 400. Nothing to disable and
  // nothing to explain in a tooltip — the schedule simply does not count anything.
  if (!isResettableSchedule(schedule)) return null;

  const isBusy = mutation.isPending || mutation.isLoading;

  const message = (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        {intl.formatMessage(cmmsMessages.resetConfirmSchedule, { title: schedule.title })}
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        {buildScheduleResetWarning(reading, intl, { lastResetAt })}
      </Typography>
      <TextField
        label={intl.formatMessage(cmmsMessages.resetNoteLabel)}
        helperText={intl.formatMessage(cmmsMessages.resetNoteHint)}
        value={notes}
        onChange={(event) => setNotes(event.target.value)}
        inputProps={{ maxLength: NOTE_MAX_LENGTH }}
        size="small"
        multiline
        rows={2}
        fullWidth
      />
    </Stack>
  );

  // Hidden, not disabled. The endpoint is Cmms_Manager, and a control that always 403s is a
  // control that lies about what its owner may do.
  return (
    <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
      <Box>
        <Button
          variant="outlined"
          size={size}
          fullWidth={fullWidth}
          startIcon={<RestartAltIcon />}
          onClick={() => setOpen(true)}
          disabled={isBusy}
          sx={getOutlinedButtonSx(cmmsColors)}
        >
          {intl.formatMessage(cmmsMessages.resetAction)}
        </Button>

        <AxionControls.AxionConfirmation
          open={open}
          title={intl.formatMessage(cmmsMessages.resetConfirmTitle)}
          message={message}
          confirmText={intl.formatMessage(cmmsMessages.resetConfirmButton)}
          cancelText={intl.formatMessage(cmmsMessages.cancel)}
          onConfirm={handleConfirm}
          onCancel={close}
        />
        <AxionControls.AxionBackdrop open={isBusy} />
      </Box>
    </HideIfNoRole>
  );
}
